import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Rating,
  Button,
  IconButton,
  Tooltip as MuiTooltip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  CheckCircle,
  Warning,
  Info,
  Star,
  Assignment,
  Grade,
  EmojiEvents,
  SpeedOutlined,
  TimerOutlined,
  Leaderboard,
  RocketLaunch,
  PsychologyAlt,
  Assessment,
  SchoolOutlined,
  PlayArrow,
  Analytics,
  Psychology,
  Flag
} from '@mui/icons-material';

// Helper function for gradient colors
const getGradientColors = (variant, mode) => {
  const gradients = {
    default: {
      light: { start: '#f8f9ff', end: '#e8f4fd' },
      dark: { start: '#2c3e50', end: '#34495e' }
    },
    success: {
      light: { start: '#f0f8f0', end: '#e0f2e0' },
      dark: { start: '#2d5a2d', end: '#3a6b3a' }
    },
    warning: {
      light: { start: '#fef7e0', end: '#fde7b0' },
      dark: { start: '#8b4513', end: '#a0522d' }
    },
    info: {
      light: { start: '#f0f8ff', end: '#e0f0ff' },
      dark: { start: '#4a6b8a', end: '#5a7b9a' }
    },
    secondary: {
      light: { start: '#f8f0ff', end: '#f0e0ff' },
      dark: { start: '#5d4e75', end: '#6d5e85' }
    },
    error: {
      light: { start: '#fef5f0', end: '#ffe8e0' },
      dark: { start: '#8b5a3c', end: '#9b6a4c' }
    },
    primary: {
      light: { start: '#f0f8f0', end: '#e0f2e0' },
      dark: { start: '#4a7c59', end: '#5a8c69' }
    }
  };

  return gradients[variant]?.[mode] || gradients.default[mode];
};

// Reusable Metric Card Component
export const MetricCard = ({ 
  title, 
  value, 
  subtitle, 
  icon, 
  color = 'primary.main', 
  variant = 'default',
  mode,
  trend,
  progress,
  target,
  customContent
}) => {
  const cardStyles = {
    width: '100%',
    height: '100%',
    minHeight: { xs: '140px', sm: '160px', md: '180px', lg: '200px' },
    maxHeight: { xs: '140px', sm: '160px', md: '180px', lg: '200px' },
    background: mode === 'dark' 
      ? `linear-gradient(135deg, ${getGradientColors(variant, mode).start}, ${getGradientColors(variant, mode).end})`
      : `linear-gradient(135deg, ${getGradientColors(variant, mode).start}, ${getGradientColors(variant, mode).end})`,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: { xs: 1, sm: 2 },
    boxShadow: mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.1)'
  };

  const contentStyles = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    p: { xs: 1.5, sm: 2, md: 2.5 },
    '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
  };

  const avatarStyles = {
    bgcolor: color,
    width: { xs: 48, sm: 56, md: 64 },
    height: { xs: 48, sm: 56, md: 64 },
    boxShadow: 3
  };

  return (
    <Card elevation={4} sx={cardStyles}>
      <CardContent sx={contentStyles}>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
          <Box>
            <Typography color="text.secondary" gutterBottom sx={{ 
              fontWeight: 500,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              {title}
            </Typography>
            <Typography variant="h3" component="div" sx={{ 
              fontWeight: 'bold', 
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' }
            }}>
              {value}
            </Typography>
            {trend && (
              <Box display="flex" alignItems="center" gap={1}>
                {trend.icon}
                <Typography variant="body2" color={trend.color}>
                  {trend.text}
                </Typography>
              </Box>
            )}
            {progress && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={progress.value}
                  color={progress.color || 'success'}
                  sx={{ height: 6, borderRadius: 3, mb: 1 }}
                />
                {target && (
                  <Typography variant="body2" color="text.secondary">
                    Target: {target}
                  </Typography>
                )}
              </>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {customContent && (
              <Box mt={1}>
                {customContent}
              </Box>
            )}
          </Box>
          <Avatar sx={avatarStyles}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// Reusable Loading Component
export const LoadingSpinner = ({ size = 60, message = 'Loading...' }) => (
  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh">
    <CircularProgress size={size} />
    {message && (
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
        {message}
      </Typography>
    )}
  </Box>
);

// Reusable Error Component
export const ErrorDisplay = ({ error, title = 'Error' }) => (
  <Container maxWidth="lg" sx={{ mt: 4 }}>
    <Alert severity="error">
      <AlertTitle>{title}</AlertTitle>
      {error}
    </Alert>
  </Container>
);

// Reusable Empty State Component
export const EmptyState = ({ 
  icon, 
  title, 
  description, 
  action = null,
  iconSize = 60 
}) => (
  <Box textAlign="center" py={4}>
    {React.cloneElement(icon, { 
      sx: { fontSize: iconSize, color: 'text.secondary', mb: 2 } 
    })}
    <Typography variant="h6" color="text.secondary" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" mb={3}>
      {description}
    </Typography>
    {action}
  </Box>
);

// Reusable Performance List Item Component
export const PerformanceListItem = ({ 
  test, 
  mode, 
  showDetails = true,
  onItemClick = null 
}) => {
  const getPerformanceColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const listItemStyles = {
    borderRadius: 2,
    mb: 1,
    bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    cursor: onItemClick ? 'pointer' : 'default',
    '&:hover': onItemClick ? {
      bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
    } : {}
  };

  return (
    <ListItem sx={listItemStyles} onClick={onItemClick}>
      <ListItemIcon>
        <Avatar sx={{ 
          bgcolor: getPerformanceColor(test.score) + '.main',
          boxShadow: 2
        }}>
          {test.score >= 80 ? <CheckCircle /> : <Warning />}
        </Avatar>
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="h6" fontWeight="bold">
            {test.testName || `Test ${test.id}`}
          </Typography>
        }
        secondary={
          showDetails ? (
            <Box>
              <Box display="flex" gap={3} mb={1}>
                <Typography variant="body2" component="span" fontWeight="bold">
                  Score: {test.score?.toFixed(1) || 'N/A'}%
                </Typography>
                <Typography variant="body2" component="span">
                  Accuracy: {test.accuracy_rate?.toFixed(1) || 'N/A'}%
                </Typography>
                <Typography variant="body2" component="span">
                  Rank: #{test.all_india_rank || 'N/A'}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={test.score || 0}
                color={getPerformanceColor(test.score || 0)}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Score: {test.score?.toFixed(1) || 'N/A'}%
            </Typography>
          )
        }
      />
      {showDetails && (
        <Box textAlign="right">
          <Chip
            label={test.created_at ? new Date(test.created_at).toLocaleDateString() : 'Recent'}
            size="small"
            variant="outlined"
            color="primary"
          />
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              {test.speed_score ? `${test.speed_score.toFixed(1)} q/min` : 'Speed N/A'}
            </Typography>
          </Box>
        </Box>
      )}
    </ListItem>
  );
};

// Reusable Chart Container Component
export const ChartContainer = ({ 
  children, 
  height = 400, 
  title = null,
  noDataMessage = 'No data available'
}) => (
  <Box height={height}>
    {children || (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="text.secondary">{noDataMessage}</Typography>
      </Box>
    )}
  </Box>
);

// Reusable Tab Panel Component
export const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && <Box p={3}>{children}</Box>}
  </div>
);

// Reusable Action Button Component
export const ActionButton = ({ 
  variant = 'primary', 
  size = 'medium',
  startIcon,
  children,
  ...props 
}) => {
  const buttonStyles = {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 2,
    ...(variant === 'primary' && {
      background: 'linear-gradient(45deg, #7cb342, #8bc34a)',
      '&:hover': { background: 'linear-gradient(45deg, #689f38, #7cb342)' }
    })
  };

  return (
    <Button
      variant={variant}
      size={size}
      startIcon={startIcon}
      sx={buttonStyles}
      {...props}
    >
      {children}
    </Button>
  );
};

// Reusable Stats Display Component
export const StatsDisplay = ({ stats, mode }) => (
  <Grid container spacing={2}>
    {stats.map((stat, index) => (
      <Grid item xs={6} sm={6} md={3} key={index}>
        <MetricCard
          title={stat.title}
          value={stat.value}
          subtitle={stat.subtitle}
          icon={stat.icon}
          color={stat.color}
          variant={stat.variant}
          mode={mode}
          trend={stat.trend}
          progress={stat.progress}
          target={stat.target}
        />
      </Grid>
    ))}
  </Grid>
);

// Custom hook for responsive breakpoints
export const useResponsive = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [isTablet, setIsTablet] = React.useState(false);
  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 600);
      setIsTablet(width >= 600 && width < 960);
      setIsDesktop(width >= 960);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { isMobile, isTablet, isDesktop };
};

// Custom hook for theme-aware styling
export const useThemeStyles = (mode) => {
  const getTextColor = (variant = 'primary') => {
    const colors = {
      primary: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
      secondary: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      disabled: mode === 'dark' ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)'
    };
    return colors[variant];
  };

  const getBackgroundColor = (variant = 'default') => {
    const colors = {
      default: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      hover: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
      selected: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)'
    };
    return colors[variant];
  };

  return { getTextColor, getBackgroundColor };
}; 